import { useState } from 'react';

import { SubmitSuggestionRequestSchema } from '@smartapply/shared';

import { submitSuggestion, SiteApiError } from '../lib/api.js';
import { Stamp } from './Stamp.js';

export function SuggestionSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const charCount = content.length;
  const tooShort = charCount > 0 && charCount < 20;
  const tooLong = charCount > 2000;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const parsed = SubmitSuggestionRequestSchema.safeParse({
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      content: content.trim(),
    });

    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setError(first?.message ?? 'Algum campo está inválido.');
      return;
    }

    setBusy(true);
    try {
      const res = await submitSuggestion(parsed.data);
      setSuccess(res.message);
      setName('');
      setEmail('');
      setContent('');
    } catch (e) {
      setError(e instanceof SiteApiError ? e.message : 'Falha ao enviar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="suggest" className="border-b border-[var(--border-subtle)] py-24 sm:py-32">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1fr_1.2fr]">
        <header>
          <Stamp variant="suggestion">canal aberto</Stamp>
          <h2 className="mt-4 font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[44px]">
            Falta alguma coisa<span className="text-[var(--accent-saffron)]">?</span>
          </h2>
          <p className="mt-4 font-sans text-base leading-relaxed text-[var(--ink-secondary)]">
            Manda sua sugestão. Se for tecnicamente viável e fizer sentido pro produto, ela vira
            um item novo no roadmap aqui em cima, e aí a comunidade vota pra colocar na fila de
            implementação.
          </p>

          <div className="mt-8 space-y-4 border-l-2 border-[var(--accent-saffron)] pl-4">
            <p className="font-sans text-sm leading-relaxed text-[var(--ink-secondary)]">
              <strong className="font-medium text-[var(--ink-primary)]">Boas sugestões:</strong> "queria
              gerar posts a partir de um vídeo do YouTube", "exportar análise como PDF estilizado",
              "comparar minha cadência com benchmarks da minha área".
            </p>
            <p className="font-sans text-sm leading-relaxed text-[var(--ink-secondary)]">
              <strong className="font-medium text-[var(--ink-primary)]">Sugestões que não vão pra
              frente:</strong> publicar automaticamente sem você confirmar (privacidade), scrape massivo
              do LinkedIn (ToS), modo "sem precisar de IA" (não é o produto).
            </p>
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="card-paper rounded-sm px-7 py-7"
          style={{ borderLeft: '3px solid var(--ink-primary)' }}
        >
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="sg-name"
                  className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
                >
                  Nome (opcional)
                </label>
                <input
                  id="sg-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  placeholder="como prefere ser chamado"
                  className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
                />
              </div>
              <div>
                <label
                  htmlFor="sg-email"
                  className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
                >
                  E-mail (opcional)
                </label>
                <input
                  id="sg-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={254}
                  placeholder="pra avisar quando virar feature"
                  className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="sg-content"
                className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
              >
                <span>Sugestão</span>
                <span
                  className="tabular-nums"
                  style={{
                    color: tooLong
                      ? 'var(--accent-crimson)'
                      : tooShort
                        ? 'var(--accent-saffron)'
                        : 'var(--ink-muted)',
                  }}
                >
                  {charCount} / 2000
                </span>
              </label>
              <textarea
                id="sg-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
                placeholder="descreva o que você gostaria de ver. Quanto mais concreto, melhor. exemplos: 'quero comparar duas análises lado a lado', 'gerar posts a partir de áudio'..."
                className="mt-2 w-full resize-y rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
              />
              {tooShort && (
                <p className="mt-1 font-sans text-xs text-[var(--accent-saffron)]">
                  Conte um pouco mais. Mínimo 20 caracteres.
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-sm border border-[var(--accent-crimson-border)] bg-[var(--accent-crimson-bg)] px-4 py-2.5">
                <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-sm border border-[var(--accent-forest-border)] bg-[var(--accent-forest-bg)] px-4 py-3">
                <p className="font-sans text-sm text-[var(--accent-forest)]">{success}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="font-sans text-xs italic text-[var(--ink-tertiary)]">
                Sem cadastro, sem confirmação por e-mail. Só envie.
              </p>
              <button
                type="submit"
                disabled={busy || charCount < 20 || tooLong}
                className="rounded-sm bg-[var(--ink-primary)] px-6 py-2 font-sans text-sm font-medium text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {busy ? 'Enviando...' : 'Enviar sugestão'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
